import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  AdminPageHeader,
  AdminSegmented,
  AdminStatCard,
  AdminSection,
  AdminPageSkeleton,
  AdminHeroMetric,
  AdminHeroAsideStat,
} from "@/components/admin/AdminUI";

type Range = "7d" | "30d" | "90d" | "12m" | "all";

const RANGE_OPTS: { value: Range; label: string }[] = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "12m", label: "12m" },
  { value: "all", label: "All" },
];

const RANGE_LABELS: Record<Range, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "12m": "Last 12 months",
  "all": "All time",
};

const fmtUSD = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(n);

export default function AdminProductRevenue() {
  const [range, setRange] = useState<Range>("30d");

  const { data: product, isLoading: pLoading } = useQuery({
    queryKey: ["admin-product-metrics", range],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-dashboard", {
        body: { action: "product_metrics", range },
      });
      if (error) throw error;
      return data as any;
    },
  });
  const { data: revenue, isLoading: rLoading } = useQuery({
    queryKey: ["admin-revenue-metrics", range],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-dashboard", {
        body: { action: "revenue_metrics", range },
      });
      if (error) throw error;
      return data as any;
    },
  });

  // Activation = % of signups in the last 90 days (with ≥14d to convert)
  // who exported at least one proposal within 14 days of signup.
  const { data: activation, isLoading: aLoading } = useQuery({
    queryKey: ["admin-activation-rate"],
    queryFn: async () => {
      const now = new Date();
      const cohortStart = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const cohortEnd = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString();

      const [{ data: signups }, { data: exports }] = await Promise.all([
        supabase.from("profiles").select("id, created_at").gte("created_at", cohortStart).lte("created_at", cohortEnd),
        supabase.from("proposal_exports").select("user_id, created_at").gte("created_at", cohortStart),
      ]);

      const cohort = signups ?? [];
      const exportsByUser = new Map<string, string[]>();
      (exports ?? []).forEach((e: any) => {
        const arr = exportsByUser.get(e.user_id) ?? [];
        arr.push(e.created_at);
        exportsByUser.set(e.user_id, arr);
      });

      let activated = 0;
      cohort.forEach((u: any) => {
        const signedUp = new Date(u.created_at).getTime();
        const deadline = signedUp + 14 * 24 * 60 * 60 * 1000;
        const userExports = exportsByUser.get(u.id) ?? [];
        if (userExports.some((t) => {
          const ts = new Date(t).getTime();
          return ts >= signedUp && ts <= deadline;
        })) {
          activated++;
        }
      });

      return {
        cohortSize: cohort.length,
        activated,
        rate: cohort.length === 0 ? null : (activated / cohort.length) * 100,
      };
    },
  });

  const todayDau = product?.dau?.[0]?.active_users ?? 0;
  const todayMau = product?.mau?.[0]?.active_users ?? 0;
  const r7 = product?.retention_d7?.[0];
  const r30 = product?.retention_d30?.[0];

  if (pLoading || rLoading || aLoading) {
    return <AdminPageSkeleton stats={4} rows={0} columns={0} />;
  }


  return (
    <div className="space-y-10">
      <AdminPageHeader
        eyebrow="Analytics"
        title="Product & Revenue"
        description="In-app engagement, retention, and recurring revenue health."
        meta={RANGE_LABELS[range]}
        actions={<AdminSegmented value={range} onChange={(v) => setRange(v as Range)} options={RANGE_OPTS} size="sm" />}
      />

      {/* Revenue hero — ARR commands the page */}
      <AdminHeroMetric
        eyebrow="Annual Recurring Revenue"
        value={fmtUSD(revenue?.arr?.arr_usd)}
        caption={
          revenue?.arr?.mrr_usd != null
            ? `MRR of ${fmtUSD(revenue.arr.mrr_usd)} compounded annually. Snapshot of every active subscription at this moment.`
            : "No subscription data yet."
        }
        aside={
          <>
            <AdminHeroAsideStat
              label="NRR"
              value={revenue?.nrr?.nrr_pct == null ? "—" : `${revenue.nrr.nrr_pct}%`}
            />
            <AdminHeroAsideStat
              label="LTV"
              value={fmtUSD(revenue?.ltv?.ltv_usd)}
            />
          </>
        }
      />

      <AdminSection title="Engagement" description={`Activity across ${RANGE_LABELS[range].toLowerCase()}.`}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <AdminStatCard label="DAU" value={String(todayDau)} sub="Latest day in range" />
          <AdminStatCard label="MAU" value={String(todayMau)} sub="Rolling 30 days" />
          <AdminStatCard
            label="Activation rate"
            value={activation?.rate == null ? "—" : `${activation.rate.toFixed(0)}%`}
            sub={
              activation?.cohortSize
                ? `${activation.activated}/${activation.cohortSize} exported within 14d`
                : "90-day cohort · 14d window"
            }
            tone="success"
          />
          {(() => {
            const hrs = revenue?.ttfv_median_hours;
            const hasData = typeof hrs === "number" && hrs > 0;
            const value = !hasData
              ? "—"
              : hrs < 48
                ? `${hrs.toFixed(1)} hrs`
                : `${(hrs / 24).toFixed(1)} days`;
            return (
              <AdminStatCard
                label="Time to first export"
                value={value}
                sub={hasData ? "Median hours, signup → first export" : "Not enough data yet"}
                tone="info"
              />
            );
          })()}
          <AdminStatCard
            label="D7 retention"
            value={r7 ? `${r7.retention_pct}%` : "—"}
            sub={r7 ? `${r7.retained_d7}/${r7.cohort_size} returners` : "Latest cohort"}
            tone="info"
          />
        </div>
      </AdminSection>


      <AdminSection title="Revenue health" description="Movement in and out of the customer base.">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <AdminStatCard
            label="ARPU"
            value={fmtUSD(revenue?.arpu_usd)}
            sub={
              revenue?.active_paying_orgs
                ? `Across ${revenue.active_paying_orgs} paying org${revenue.active_paying_orgs === 1 ? "" : "s"}`
                : "Avg monthly revenue per paying org"
            }
            tone="info"
          />
          <AdminStatCard
            label="D30 retention"
            value={r30 ? `${r30.retention_pct}%` : "—"}
            sub={r30 ? `${r30.retained_d30}/${r30.cohort_size} returners` : "Latest cohort"}
            tone="info"
          />
          <AdminStatCard
            label="Monthly churn"
            value={
              revenue?.ltv?.monthly_churn_rate == null
                ? "—"
                : `${(revenue.ltv.monthly_churn_rate * 100).toFixed(2)}%`
            }
            tone="warning"
          />
          <AdminStatCard
            label="Movement"
            value={`+${revenue?.nrr?.upgrades ?? 0} / −${revenue?.nrr?.downgrades ?? 0}`}
            sub={`${revenue?.nrr?.churned ?? 0} churned`}
          />
        </div>
      </AdminSection>
    </div>
  );
}
